'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';

import { operations } from '@/api/v1/query';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { parseExternalId } from '@/lib/external_id';
import { formatAddress, formatPhoneNumber } from '@/lib/format';

type ApiCustomer = ApiSchemas['CustomerRead'];
type ApiCustomerPartialWithId = Partial<ApiCustomer> & { id: number };

export type Customer = ApiCustomer & {
  formatted: {
    mainName: string;
    secondaryName: string;
    address: string;
    primaryPhone: string;
    primaryEmail: string;
    externalId: string;
  };
};

export function useCustomerCtx() {
  const ctx = useContext(CustomerContext);
  if (!ctx) {
    throw new Error('useCustomerCtx must be used within a CustomerProvider');
  }
  return ctx;
}

export function useCustomer(id: string) {
  const { customers } = useCustomerCtx();
  return customers[id] || CustomerSkeleton;
}

export function useCustomers(ids: string[]) {
  const { customers } = useCustomerCtx();
  return useMemo(() => ids.map((id) => customers[id]).filter(Boolean), [customers, ids]);
}

type CustomersByID = Record<string, Customer>;

interface CustomerContextValue {
  customers: CustomersByID;
  upsertOne: (customer: ApiCustomerPartialWithId) => void;
  upsertMany: (customers: ApiCustomer[]) => void;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customers, setCustomers] = useState<CustomersByID>({});

  const upsertOne = useCallback((customer: ApiCustomerPartialWithId) => {
    setCustomers((prev) => {
      const existing = prev[customer.id] || {};
      const updated = { ...existing, ...customer };
      return { ...prev, [customer.id]: mapApiCustomerToCustomer(updated) };
    });
  }, []);

  const upsertMany = useCallback((customers: ApiCustomer[]) => {
    setCustomers((prev) => {
      const newCustomers = customers.reduce<CustomersByID>((acc, c) => {
        acc[c.id] = mapApiCustomerToCustomer(c);
        return acc;
      }, {});
      return {
        ...prev,
        ...newCustomers,
      };
    });
  }, []);

  return <CustomerContext.Provider value={{ customers, upsertOne, upsertMany }}>{children}</CustomerContext.Provider>;
}

type ApiCustomersListRequest = operations['search_customers']['parameters']['query'];

export function useApiCustomers(params: ApiCustomersListRequest) {
  const { upsertMany } = useCustomerCtx();
  const { api } = useApiV1Ctx();
  const result = api.customers.searchCustomers.useQuery(
    {
      query: {
        query: params?.query,
        limit: params?.limit,
        page: params?.page,
      },
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    }
  );

  useEffect(() => {
    if (result?.data?.items) {
      upsertMany(result.data.items);
    }
  }, [result?.data?.items, upsertMany]);

  return result;
}

export function useApiCustomersByIds(ids: string[]) {
  const { upsertMany } = useCustomerCtx();
  const { api } = useApiV1Ctx();
  const result = api.customers.getCustomers.useQuery(
    {
      query: {
        customer_ids: ids,
      },
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
      enabled: ids.length > 0,
    }
  );
  useEffect(() => {
    if (result?.data) {
      upsertMany(result.data);
    }
  }, [result?.data, upsertMany]);
  return result;
}

function mapApiCustomerToCustomer(customer: ApiCustomer): Customer {
  function getContactValue(type: 'PHONE' | 'EMAIL') {
    const primary = customer.contacts.find((contact) => contact.type === type && contact.primary)?.value;
    if (primary) {
      return primary;
    }
    return customer.contacts.find((contact) => contact.type === type)?.value;
  }

  const primaryLocation = customer.locations.find((location) => location.primary) ?? customer.locations[0];

  const primaryPhone = getContactValue('PHONE');
  const primaryEmail = getContactValue('EMAIL');

  const fullName = `${customer.first_name} ${customer.last_name}`.trim();
  let mainName = fullName;
  let secondaryName = '';
  if (customer.company) {
    mainName = customer.company;
    secondaryName = fullName;
  }

  return {
    ...customer,
    formatted: {
      mainName,
      secondaryName,
      address: primaryLocation ? formatAddress(primaryLocation) : '',
      primaryPhone: primaryPhone ? formatPhoneNumber(primaryPhone) : '',
      primaryEmail: primaryEmail ?? '',
      externalId: parseExternalId(customer.external_id, 'customer') ?? '',
    },
  };
}

const CustomerSkeleton: Customer = {
  id: '',
  office_id: '',
  status: 'ACTIVE',
  created_at: '',
  updated_at: '',
  first_name: '',
  last_name: '',
  contacts: [],
  commercial: false,
  locations: [],
  preferences: {
    technicians: [],
    days: [],
    dates: [],
    enforce_days: false,
    use_invoice_mail_delivery: false,
  },
  formatted: {
    mainName: '',
    secondaryName: '',
    address: '',
    primaryPhone: '',
    primaryEmail: '',
    externalId: '',
  },
};
